import "./App.css";
import "./components/AIAnalysis.css";
import Header from "./components/Header";
import TabBar from "./components/TabBar";
import SentimentBar from "./components/SentimentBar";
import EmailInfo from "./components/EmailInfo";
import SalesforceContext from "./components/SalesforceContext";
import ActivePolicies from "./components/ActivePolicies";
import CrossSell from "./components/CrossSell";
import Footer from "./components/Footer";
import mockData from "./data/mockData";
import AIAnalysis from "./components/AiAnalysis";
import Actions from "./components/Actions";
import TemplatesTab from "./components/TemplatesTab";
import AnalyticsModal from "./components/AnalyticsModal";
import apiService from "./services/apiService";
import { getOutlookItemContext, getOutlookUserProfile } from "./services/outlookContext";
import API_BASE_URL from "./config";
import { useCallback, useEffect, useRef, useState } from "react";
import Settings from "./components/Settings";

/* global Office */

const SENTIMENT_CONFIG = {
  Urgent: { emoji: "🔴", color: "#ef4444" },
  Negative: { emoji: "😟", color: "#ef4444" },
  Neutral: { emoji: "😐", color: "#f59e0b" },
  Positive: { emoji: "😊", color: "#059669" },
};

const INITIAL_SEARCH_RESULTS_COUNT = 5;
const createEmptyCrossSell = () => ({
  count: 0,
  opportunities: [],
});

const createInitialMailboxContext = () => {
  const { userEmail: actorEmail, userName: actorName } = getOutlookUserProfile();

  return {
    messageId: "",
    userEmail: actorEmail,
    actorEmail,
    actorName,
    mailboxOwner: "",
    targetMailbox: "",
    isSharedContext: false,
  };
};

const App = () => {
  const [activeTab, setActiveTab] = useState("Context");
  const [emailInfo, setEmailInfo] = useState(mockData.emailInfo);
  const [sentiment, setSentiment] = useState(mockData.sentiment);
  const [contextLoading, setContextLoading] = useState(false);
  const [sfLoading, setSfLoading] = useState(false);
  const [sfContext, setSfContext] = useState({});
  const [policies, setPolicies] = useState([]);
  const [sfAuthError, setSfAuthError] = useState(null);
  const [sfAuthRefreshing, setSfAuthRefreshing] = useState(false);
  const [sfSession, setSfSession] = useState({ accessToken: null, instanceUrl: null });
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showAllResults, setShowAllResults] = useState(false);
  const [showSearchUI, setShowSearchUI] = useState(false);
  const [manualSearchAttempted, setManualSearchAttempted] = useState(false);
  const [pendingSfMatch, setPendingSfMatch] = useState(null);
  const [crossSell, setCrossSell] = useState(createEmptyCrossSell);
  const [detectedCaseType, setDetectedCaseType] = useState("");
  const [crossSellLoading, setCrossSellLoading] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [mailboxContext, setMailboxContext] = useState(createInitialMailboxContext);
  const sfSessionRef = useRef({ accessToken: null, instanceUrl: null });
  const crossSellRequestRef = useRef(0);
  const lastSfMatchKeyRef = useRef("");
  const currentMessageIdRef = useRef("");
  const mailboxContextRef = useRef(mailboxContext);
  const itemLoadRequestRef = useRef(0);
  const itemChangedHandlerRef = useRef(null);
  const analyticsUserEmail = mailboxContext.actorEmail || getOutlookUserProfile().userEmail;

  const resetManualSearchState = useCallback(() => {
    setSearchTerm("");
    setSearchResults([]);
    setShowAllResults(false);
    setManualSearchAttempted(false);
    setSfContext((prev) => ({ ...prev, error: null }));
  }, []);

    const resetCrossSellState = useCallback(() => {
    crossSellRequestRef.current += 1;
    setCrossSell(createEmptyCrossSell());
    setCrossSellLoading(false);
  }, []);

  const resetEmailSpecificState = useCallback(() => {
    setEmailInfo({ ...mockData.emailInfo });
    setSentiment(mockData.sentiment);
    setContextLoading(false);
    setSfLoading(false);
    setSfContext({});
    setPolicies([]);
    setPendingSfMatch(null);
    setDetectedCaseType("");
    setSearchTerm("");
    setSearchResults([]);
    setShowAllResults(false);
    setShowSearchUI(false);
    setManualSearchAttempted(false);
    lastSfMatchKeyRef.current = "";
    resetCrossSellState();
  }, [resetCrossSellState]);

  const handleOpenSearchUI = useCallback(() => {

    resetManualSearchState();
    setShowSearchUI(true);
  }, [resetManualSearchState]);

  const handleCloseSearchUI = useCallback(() => {
    resetManualSearchState();
    setShowSearchUI(false);
  }, [resetManualSearchState]);

  const fetchSfToken = useCallback(async () => {
    setSfAuthRefreshing(true);
    try {
      const data = await apiService.getSalesforceToken();
      if (data.error) {
        setSfAuthError(data);
        const emptySession = { accessToken: null, instanceUrl: null };
        setSfSession(emptySession);
        sfSessionRef.current = emptySession;
        console.error("Salesforce Auth Error:", data);
        return null;
      }
      const session = { accessToken: data.access_token, instanceUrl: data.instance_url };
      setSfSession(session);
      sfSessionRef.current = session;
      setSfAuthError(null);
      console.log("Salesforce token stored in memory");
      return session;
    } catch (error) {
      console.error("Failed to initialize Salesforce session:", error);
      setSfAuthError({ error: "Failed to connect to Salesforce service" });
      const emptySession = { accessToken: null, instanceUrl: null };
      setSfSession(emptySession);
      sfSessionRef.current = emptySession;
      return null;
    } finally {
      setSfAuthRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchSfToken();

    const loadCurrentOutlookItem = async () => {
      const loadRequestId = ++itemLoadRequestRef.current;

      try {
        const item = Office.context?.mailbox?.item;
        const metadata = await getOutlookItemContext(item);

        if (loadRequestId !== itemLoadRequestRef.current) {
          return;
        }

        currentMessageIdRef.current = metadata.messageId || "";
        mailboxContextRef.current = metadata;
        setMailboxContext(metadata);

        resetEmailSpecificState();

        if (!item) {
          return;
        }

        setEmailInfo((prev) => ({
          ...prev,
          from: item.from?.displayName || "",
          email: item.from?.emailAddress || "",
        }));

        item.body.getAsync("text", async (result) => {
          if (
            loadRequestId !== itemLoadRequestRef.current ||
            currentMessageIdRef.current !== (metadata.messageId || "")
          ) {
            return;
          }

          if (result.status === "succeeded" && result.value) {
            await analyzeContext(result.value, metadata);
          }
        });
      } catch (e) {
        console.log("Failed to load selected Outlook item", e);
      }
    };

    itemChangedHandlerRef.current = loadCurrentOutlookItem;

    // 2. Office.js Initialization
    try {
      const outlookUserProfile = getOutlookUserProfile();
      if (outlookUserProfile.userEmail) {
        apiService.registerOutlookUser(outlookUserProfile).catch((error) => {
          console.error("Outlook user registration failed:", error);
        });
      }

      const userProfile = Office.context?.mailbox?.userProfile;

      if (userProfile && userProfile.emailAddress) {
        apiService.getOrCreateUser(userProfile.emailAddress, userProfile.displayName)
          .then(user => {
            console.log("Logged in user:", user);
            setCurrentUser(user);
          })
          .catch(err => {
            console.error("Failed to fetch/create user profile:", err);
          });
      }

      loadCurrentOutlookItem();

      Office.context?.mailbox?.addHandlerAsync?.(
        Office.EventType.ItemChanged,
        loadCurrentOutlookItem,
        (result) => {
          if (result.status !== Office.AsyncResultStatus.Succeeded) {
            console.warn("Failed to register Outlook ItemChanged handler", result.error);
          }
        }
      );
    } catch (e) {
      console.log("Office.js not available, using mock data");
    }

    return () => {
      try {
        if (itemChangedHandlerRef.current) {
          Office.context?.mailbox?.removeHandlerAsync?.(
            Office.EventType.ItemChanged,
            { handler: itemChangedHandlerRef.current }
          );
        }
      } catch (e) {
        // Ignore cleanup issues when Office.js is unavailable or the host is closing.
      }
    };
  }, []);

  const analyzeContext = async (emailBody, metadata = mailboxContextRef.current) => {
    const requestMessageId = metadata.messageId || "";
    setContextLoading(true);
    try {
      const data = await apiService.analyzeEmailContext({
        emailBody,
        messageId: metadata.messageId,
        userEmail: metadata.userEmail,
      });

      if (currentMessageIdRef.current && currentMessageIdRef.current !== requestMessageId) {
        return;
      }

      // Update sentiment

      if (data.sentiment) {
        const config = SENTIMENT_CONFIG[data.sentiment] || SENTIMENT_CONFIG.Neutral;
        setSentiment({
          level: data.sentiment,
          emoji: config.emoji,
          color: config.color,
          message: data.sentimentSummary || "",
        });
      }

      // Update phone and policy number from AI
      setEmailInfo((prev) => ({
        ...prev,
        phone: data.phone || prev.phone,
        policyMentioned: data.policyNumber || prev.policyMentioned,
        nameInsured: data.nameInsured || prev.nameInsured,
      }));

      // Store detected case type for AI Analysis tab
      if (data.caseType) {
        setDetectedCaseType(data.caseType);
      }

      const nextPendingSfMatch = {
        messageId: metadata.messageId || "",
        policyNumber: data.policyNumber || "",
        nameInsured: data.nameInsured || "",
        email: data.email || "",
        phone: data.phone || "",
      };

      if (
        nextPendingSfMatch.policyNumber ||
        nextPendingSfMatch.nameInsured ||
        nextPendingSfMatch.email ||
        nextPendingSfMatch.phone
      ) {
        setPendingSfMatch(nextPendingSfMatch);
      } else {
        setPendingSfMatch(null);
        setSfContext({ matchFound: false });
        setPolicies([]);
        resetCrossSellState();
      }
    } catch (error) {
      console.error("Context analysis failed:", error);
    } finally {
      if (!currentMessageIdRef.current || currentMessageIdRef.current === requestMessageId) {
        setContextLoading(false);
      }
    }
  };

  const fetchCrossSellOpportunities = useCallback(async (matchResult) => {
    const policiesForCrossSell = Array.isArray(matchResult?.policies) ? matchResult.policies : [];

    if (!policiesForCrossSell.length) {
      resetCrossSellState();
      return;
    }

    const requestId = ++crossSellRequestRef.current;
    setCrossSellLoading(true);

    try {
      let metadata = mailboxContextRef.current;

      if (!metadata.messageId || metadata.messageId !== currentMessageIdRef.current) {
        metadata = await getOutlookItemContext();
      }
      const response = await apiService.generateCrossSellOpportunities({
        messageId: metadata.messageId,
        userEmail: metadata.userEmail,
        accountName: matchResult.accountName || "",
        contactName: matchResult.contacts?.[0]?.name || "",
        clientSince: matchResult.clientSince || "",
        openCases: matchResult.openCasesCount ?? null,
        policies: policiesForCrossSell,
      });

      if (requestId !== crossSellRequestRef.current) return;

      const opportunities = Array.isArray(response?.opportunities) ? response.opportunities : [];
      setCrossSell({
        count: typeof response?.count === "number" ? response.count : opportunities.length,
        opportunities,
      });
    } catch (error) {
      console.error("Cross-sell generation failed:", error);
      if (requestId !== crossSellRequestRef.current) return;
      setCrossSell(createEmptyCrossSell());
    } finally {
      if (requestId === crossSellRequestRef.current) {
        setCrossSellLoading(false);
      }
    }
  }, [resetCrossSellState]);

  const handleSfResult = useCallback((res) => {
    if (res.status === "FOUND" || res.status === "SUCCESS") {
      const contacts = res.contacts || [];
      let matchedContact = null;

      if (contacts.length > 0) {
        // 1. Match by sender's email
        const fromEmail = emailInfo.email?.toLowerCase();
        if (fromEmail) {
          matchedContact = contacts.find(c => c.email && c.email.toLowerCase() === fromEmail);
        }

        // 2. Match by AI-extracted nameInsured
        if (!matchedContact && emailInfo.nameInsured) {
          const nameInsuredLower = emailInfo.nameInsured.toLowerCase().trim();
          const words = nameInsuredLower.split(/\s+/).filter(Boolean);
          matchedContact = contacts.find(c => {
            const cName = (c.name || "").toLowerCase();
            return words.every(word => cName.includes(word));
          });
        }

        // 3. Fallback to first contact
        if (!matchedContact) {
          matchedContact = contacts[0];
        }
      }

      setSfContext({
        matchFound: true,
        accountName: res.accountName,
        contact: matchedContact ? matchedContact.name : "N/A",
        contactId: matchedContact ? matchedContact.contactId : "",
        contacts: res.contacts || [],
        clientSince: res.clientSince,
        openCases: res.openCasesCount,
        accountId: res.accountId
      });

      const mappedPolicies = (res.policies || []).map((p, idx) => {
        const lobMap = {
          AUTOB: { icon: "🚗", type: "Auto Policy" },
          CPKGE: { icon: "📦", type: "Commercial Package" },
          CUMBR: { icon: "☂️", type: "Umbrella Policy" },
          PROP: { icon: "🏠", type: "Property Policy" },
          CGL: { icon: "🛡️", type: "General Liability" }
        };
        const info = lobMap[p.lineOfBusiness] || { icon: "📄", type: p.lineOfBusiness || "Unknown" };
        const rawPremium = p.premiumAmount;
        const formattedPremium =
          rawPremium != null && !isNaN(Number(rawPremium))
            ? `$${Number(rawPremium).toLocaleString()}`
            : null;

        return {
          id: p.policyId || idx,
          type: info.type,
          icon: info.icon,
          status: p.status || "Active",
          policyNumber: p.policyNumber || "N/A",
          carrier: p.carrier || null,
          premium: formattedPremium,
          expiration: p.expirationDate ? p.expirationDate.split(" ")[0] : null,
          details: p.lineOfBusiness || null
        };
      });
      setPolicies(mappedPolicies);
      resetManualSearchState();
      setShowSearchUI(false); // Hide search UI on successful match
      fetchCrossSellOpportunities(res);
    } else {
      setSfContext({ matchFound: false });
      setPolicies([]);
      resetCrossSellState();
    }
  }, [fetchCrossSellOpportunities, resetCrossSellState, resetManualSearchState, emailInfo.email, emailInfo.nameInsured]);

  useEffect(() => {
    if (!pendingSfMatch || sfAuthError) return;

    const { accessToken, instanceUrl } = sfSession;
    if (!accessToken || !instanceUrl) return;

    const hasLookupValue = Boolean(
      pendingSfMatch.policyNumber ||
      pendingSfMatch.nameInsured ||
      pendingSfMatch.email ||
      pendingSfMatch.phone
    );
    if (!hasLookupValue) return;

    const requestKey = JSON.stringify({
      messageId: pendingSfMatch.messageId || "",
      policyNumber: pendingSfMatch.policyNumber || "",
      nameInsured: pendingSfMatch.nameInsured || "",
      email: pendingSfMatch.email || "",
      phone: pendingSfMatch.phone || "",
    });

    if (lastSfMatchKeyRef.current === requestKey) return;
    lastSfMatchKeyRef.current = requestKey;

    console.log("Running Salesforce match for extracted context", pendingSfMatch);
    setSfLoading(true);

    apiService.matchAccount({
      instance_url: instanceUrl,
      access_token: accessToken,
      policyNumber: pendingSfMatch.policyNumber || "",
      nameInsured: pendingSfMatch.nameInsured || "",
      email: pendingSfMatch.email || "",
      phone: pendingSfMatch.phone || "",
    })
            .then((res) => {
        if (currentMessageIdRef.current && currentMessageIdRef.current !== (pendingSfMatch.messageId || "")) {
          return;
        }
        handleSfResult(res);
      })
      .catch((err) => {
        if (currentMessageIdRef.current && currentMessageIdRef.current !== (pendingSfMatch.messageId || "")) {
          return;
        }
        console.error("SFDC Match Error:", err);
        setSfContext({ matchFound: false });
        resetCrossSellState();
      })
      .finally(() => {
        if (!currentMessageIdRef.current || currentMessageIdRef.current === (pendingSfMatch.messageId || "")) {
          setSfLoading(false);
        }
      });

  }, [pendingSfMatch, sfAuthError, sfSession, handleSfResult, resetCrossSellState]);

  const handleSelectAccount = (account) => {
    const { accessToken: token, instanceUrl } = sfSessionRef.current;
    if (!token || !instanceUrl) return;

    setSfLoading(true);
    setSearchResults([]); // Clear list while loading details

    // Hit the match-account API with the selected account name
    apiService.matchAccount({
      instance_url: instanceUrl,
      access_token: token,
      nameInsured: account.accountName || account.name || "",
      policyNumber: account.policyNumber || "",
      email: account.email || "",
      phone: account.phone || "",
    })
      .then((res) => {
        handleSfResult(res);
      })
      .catch((err) => {
        console.error("SFDC Match Error after selection:", err);
        setSfContext({ matchFound: false, error: "Failed to fetch account details." });
      })
      .finally(() => {
        setSfLoading(false);
      });
  };

  const handleManualSearch = async () => {
    if (!searchTerm.trim()) return;
    const trimmedSearchTerm = searchTerm.trim();

    const { accessToken: token, instanceUrl } = sfSessionRef.current;
    if (!token || !instanceUrl) {
      const session = await fetchSfToken();
      if (!session) return;
    }

    setSfLoading(true);
    setManualSearchAttempted(true);
    try {
      setSfContext((prev) => ({ ...prev, error: null }));
      const res = await apiService.searchAccount({
        instance_url: instanceUrl || sfSessionRef.current.instanceUrl,
        access_token: token || sfSessionRef.current.accessToken,
        searchTerm: trimmedSearchTerm
      });

      if (Array.isArray(res)) {
        setSearchResults(res);
        setShowAllResults(false); // Reset show all
      } else {
        handleSfResult(res);
      }
    } catch (err) {
      console.error("SFDC Manual Search Error:", err);
      setSfContext((prev) => ({ ...prev, error: "Search failed. Please try again." }));
    } finally {
      setSfLoading(false);
    }
  };

  const isSearchingDifferentAccount = showSearchUI && sfContext.matchFound;
  const hasSearchResults = searchResults.length > 0;
  const searchStatusText = hasSearchResults
    ? `Found ${searchResults.length} Salesforce ${searchResults.length === 1 ? "account" : "accounts"}. Select one below.`
    : (isSearchingDifferentAccount && !manualSearchAttempted)
      ? "Search for a different account:"
      : "No matching Salesforce account found.";
  const visibleSearchResults = showAllResults
    ? searchResults
    : searchResults.slice(0, INITIAL_SEARCH_RESULTS_COUNT);
  const remainingResultsCount = Math.max(searchResults.length - INITIAL_SEARCH_RESULTS_COUNT, 0);
  const canManageSfSettings = currentUser?.role === "admin";
  const sfAuthGuidance = canManageSfSettings
    ? "Check Salesforce keys in Settings."
    : "Ask an admin to check Salesforce settings.";

  return (
    <div className="app">
      <Header
        onAnalyticsClick={() => {
          setShowSettings(false);
          setShowAnalytics(true);
        }}
        onSettingsClick={() => {
          setShowAnalytics(false);
          setShowSettings((prev) => !prev);
        }}
      />

      {showAnalytics ? (
        <AnalyticsModal
          userEmail={analyticsUserEmail}
          onClose={() => setShowAnalytics(false)}
        />
      ) : showSettings ? (
        <Settings currentUser={currentUser} onClose={() => setShowSettings(false)} />
      ) : (
        <>
          {sfAuthError && (
            <div className="auth-error-banner">
              <span className="auth-error-icon">⚠️</span>
              <div className="auth-error-content">
                <h3>Salesforce unavailable</h3>
                <p className="auth-error-help">{sfAuthGuidance}</p>
              </div>
              <button
                type="button"
                className="auth-refresh-btn"
                onClick={fetchSfToken}
                disabled={sfAuthRefreshing}
                aria-label={sfAuthRefreshing ? "Reconnecting to Salesforce" : "Reconnect to Salesforce"}
                title={sfAuthRefreshing ? "Reconnecting..." : "Reconnect"}
              >
                <svg
                  className={sfAuthRefreshing ? "auth-refresh-icon auth-refresh-icon--spinning" : "auth-refresh-icon"}
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 2v6h-6" />
                  <path d="M3 22v-6h6" />
                  <path d="M3.5 9a9 9 0 0 1 14.13-3.36L21 8" />
                  <path d="M20.5 15a9 9 0 0 1-14.13 3.36L3 16" />
                </svg>
              </button>
            </div>
          )}

          {activeTab !== "Templates" && sfContext?.matchFound === false && (
            <div className="sf-not-found-banner">
              <h3 className="sf-not-found-heading">Salesforce account not found</h3>
            </div>
          )}

          <TabBar activeTab={activeTab} onTabChange={setActiveTab} />

          <div className="tab-content">
            {activeTab === "Context" && (
              <div className="context-tab">
                {contextLoading && (
                  <div className="context-loading">
                    <span className="spinner" /> Analyzing email...
                  </div>
                )}
                {sentiment.level && <SentimentBar sentiment={sentiment} />}
                <EmailInfo emailInfo={emailInfo} />

                {sfLoading ? (
                  <div className="section-loading">
                    <span className="spinner" /> Searching Salesforce...
                  </div>
                ) : (sfContext.matchFound && !showSearchUI) ? (
                  <>
                    <div className="section-header-row">
                      <h3 className="section-title">{sfContext.accountName || "Salesforce Account"}</h3>
                      <button className="text-action-link" onClick={handleOpenSearchUI}>Change</button>
                    </div>
                    <SalesforceContext context={sfContext} />
                    <ActivePolicies policies={policies} />
                    <CrossSell crossSell={crossSell} loading={crossSellLoading} />
                  </>
                ) : (sfContext.matchFound === false || showSearchUI) ? (
                  <div className="no-match-card">
                    {isSearchingDifferentAccount && (
                      <button className="back-link" onClick={handleCloseSearchUI}>← Back to current account</button>
                    )}
                    <span className="no-match-icon">☁️</span>
                    <p>{searchStatusText}</p>

                    <div className="manual-search-box">
                      <input
                        type="text"
                        placeholder="Search by name, policy, or email..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleManualSearch()}
                      />
                      <button onClick={handleManualSearch} disabled={sfLoading}>
                        {sfLoading ? "Searching..." : "Search"}
                      </button>
                    </div>

                    {searchResults.length > 0 && (
                      <div className="search-results-list">
                        <p className="search-results-title">Select an account:</p>
                        <div className="search-results-scroll-container">
                          {visibleSearchResults.map((acc, idx) => (
                            <div key={acc.accountId || idx} className="search-result-item" onClick={() => handleSelectAccount(acc)}>
                              <div className="search-result-name">{acc.accountName || acc.name}</div>
                              <div className="search-result-details">
                                {acc.policyNumber && <span>Policy: {acc.policyNumber}</span>}
                                {acc.email && <span> | {acc.email}</span>}
                              </div>
                            </div>
                          ))}
                        </div>

                        {remainingResultsCount > 0 && !showAllResults && (
                          <button className="show-more-btn" onClick={() => setShowAllResults(true)}>
                            View more ({remainingResultsCount} more)
                          </button>
                        )}
                      </div>
                    )}

                    {sfContext.error && <p className="search-error">{sfContext.error}</p>}
                  </div>
                ) : null}
              </div>
            )}

            {activeTab === "AI Analysis" && (
              <div className="context-tab">
                                <AIAnalysis
                  sfContext={sfContext}
                  policies={policies}
                  sfAuthError={sfAuthError}
                  sfSession={sfSession}
                  refreshSfToken={fetchSfToken}
                  detectedCaseType={detectedCaseType}
                  currentMessageId={currentMessageIdRef.current}
                  mailboxContext={mailboxContext}
                />

              </div>
            )}

            {activeTab === "Actions" && (
              <Actions
                onNavigateToTab={setActiveTab}
                sfContext={sfContext}
                sfAuthError={sfAuthError}
                emailInfo={emailInfo}
                sfSession={sfSession}
                refreshSfToken={fetchSfToken}
              />
            )}

            {activeTab === "Templates" && (
              <TemplatesTab
                emailInfo={emailInfo}
                salesforceContext={sfContext}
                sfAuthError={sfAuthError}
              />
            )}
          </div>

          <Footer />
        </>
      )}
    </div>
  );
};

export default App;
